import { ErrorMessage, Field, FieldArray } from 'formik';
import { useMemo, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { FormLabel, FormStepHeader } from '../../../../../components/forms';
import { FormSelect } from '../../../../../components/tables/filterForms/FormSelect';
import { useToast } from '../../../../../context/ToastContext';
import { useGetAdminsQuery } from '../../../../../services/endpoints/people/user';
import { useGetLocationMapGroupsQuery, useCreateLocationMapGroupMutation, useGetLocationsMapQuery } from '../../../../../services/endpoints/places/location';
import { DocumentElement } from '../../schedule/sessions/NewSessionSteps/DocumentsAndFilesForm';
import { StepProps } from './AddEditLocation';
import { Alert, Button, Card, Col, Form, FormControl, Row, Stack } from 'react-bootstrap';
import MapImg from '../../../../../assets/images/nationwide-map.png';
import { NumberInput } from '../../../../../components/inputs/NumberInput';
import { MapPinLayer } from '../../../../../components/map/MapPinLayer';
import { usTimezones } from '../../../../../utils/consts';

// export interface StepProps {
//   errors: Partial<FormikErrors<FormValues>>;
//   touched: Partial<FormikTouched<FormValues>>;
//   values?: FormValues;
//   setFieldValue?: (field: string, value: any, shouldValidate?: boolean) => void;
//   readOnly?: boolean;
// }

export const LocationForm = ({ touched, errors, values, editing, setFieldValue }: StepProps) => {
  // Area Director options: active Admins + Location Admins for this location.
  // API accepts one scope per request, so fetch both and merge.
  const adminQueryArgs = {
    data: {
      entries: 10000,
      orderBy: { field: 'first_name', order: 'asc' },
      ...(values?.id
        ? { filterBy: [{ field: 'location_id', value: values.id, operator: 'equals' as const }] }
        : {})
    }
  };
  const { data: admins, error: adminsError } = useGetAdminsQuery({
    ...adminQueryArgs,
    scope: 'admins'
  });
  const { data: locationAdmins, error: locationAdminsError } = useGetAdminsQuery({
    ...adminQueryArgs,
    scope: 'location_admins'
  });
  const areaDirectorOptions = useMemo(() => {
    const byId = new Map<number, { value: number; label: string }>();
    [...(admins?.results ?? []), ...(locationAdmins?.results ?? [])]
      .filter((admin) => !!admin.active)
      .forEach((admin) => {
        byId.set(admin.id, { value: admin.id, label: admin.full_name });
      });
    return Array.from(byId.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [admins?.results, locationAdmins?.results]);
  const readOnly = false;
  const [fileName, setFileName] = useState<string>('');
  const [documentType, setDocumentType] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File>();
  const slugManuallyEdited = useRef(false);
  const onDrop = (acceptedFiles: File[]) => {
    // console.log(...acceptedFiles, values);
    // setFieldValue('files', [...values!.files, ...acceptedFiles]);
    setSelectedFile(acceptedFiles[0]);
  };


  const { addToast } = useToast();

  return (
      <>
        <Row>
          <Col xs={12}>
            <FormStepHeader title="Location Details" />
          </Col>
        </Row>
        <Row>
          <Col
              xs={6}
              className="pe-1"
          >
            <Form.Group
                className="mb-lg-3 mb-1"
                controlId="title"
            >
              <FormLabel required>Title</FormLabel>
              <Field
                  name="title"
                  type="text"
                  as={Form.Control}
                  isInvalid={touched.title && !!errors.title}
                  aria-required
              />
              <ErrorMessage name="title">
                {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
              </ErrorMessage>
            </Form.Group>

            <Form.Group
                className="mb-lg-3 mb-1"
                controlId="slug"
            >
              <FormLabel required>URL Slug</FormLabel>
              <Field
                  name="slug"
                  type="text"
                  as={Form.Control}
                  isInvalid={touched.slug && !!errors.slug}
                  placeholder="e.g. san_diego"
                  aria-required
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    slugManuallyEdited.current = true;
                    const cleaned = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
                    setFieldValue!('slug', cleaned);
                  }}
              />
              <Form.Text className="text-muted">
                Lowercase letters, numbers, and underscores only.
              </Form.Text>
              {editing && touched.slug && (
                  <Alert variant="warning" className="mt-2 mb-0 py-2 px-3" style={{ fontSize: '0.85rem' }}>
                    <strong>Heads up:</strong> Changing the URL slug will break any existing links to this location's microsite. Make sure to update any shared or published URLs after saving.
                  </Alert>
              )}
              <ErrorMessage name="slug">
                {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
              </ErrorMessage>
            </Form.Group>

            <Form.Group
                className="mb-lg-3 mb-1"
                controlId="business_entity_name"
            >
              <FormLabel>Business Entity Name</FormLabel>
              <Field
                  name="business_entity_name"
                  type="text"
                  as={Form.Control}
                  isInvalid={touched.business_entity_name && !!errors.business_entity_name}
              />
              <ErrorMessage name="business_entity_name">
                {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
              </ErrorMessage>
            </Form.Group>

            <Form.Group
                className="mb-lg-3 mb-1"
                controlId="phone_number"
            >
              <FormLabel>Business Phone Number</FormLabel>
              <Field
                  name="phone_number"
                  type="text"
                  as={Form.Control}
                  isInvalid={touched.phone_number && !!errors.phone_number}
              />
              <ErrorMessage name="phone_number">
                {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
              </ErrorMessage>
            </Form.Group>

            <Form.Group
                className="mb-lg-3 mb-1"
                controlId="primary_city"
            >
              <FormLabel required>Primary City</FormLabel>
              <Field
                  name="primary_city"
                  type="text"
                  as={Form.Control}
                  isInvalid={touched.primary_city && !!errors.primary_city}
                  aria-required
              />
              <ErrorMessage name="primary_city">
                {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
              </ErrorMessage>
            </Form.Group>

            <Form.Group
                className="mb-lg-3 mb-1"
                controlId="is_active"
            >
              <FormLabel>Is Active</FormLabel>
              <Field
                  name="is_active"
                  checked={values?.is_active}
                  as={Form.Check}
                  isInvalid={touched.is_active && !!errors.is_active}
              />
              <ErrorMessage name="is_active">
                {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
              </ErrorMessage>
            </Form.Group>
          </Col>
          <Col
              xs={6}
              className="ps-1"
          >
            <Form.Group
                className="mb-lg-3 mb-1"
                controlId="manager_id"
            >
              <FormSelect
                  name="manager_id"
                  label="Area Director"
                  options={areaDirectorOptions}
                  loadingError={
                    adminsError || locationAdminsError ? 'Error loading admins' : undefined
                  }
              />
              <ErrorMessage name="manager_id">
                {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
              </ErrorMessage>
            </Form.Group>
            <Form.Group
                className="mb-lg-3 mb-1"
                controlId="timezone"
            >
              <FormLabel required={!!values?.is_active}>Time Zone</FormLabel>
              <Field
                  name="timezone"
                  as={Form.Select}
                  isInvalid={touched.timezone && !!errors.timezone}
                  aria-required={values?.is_active || undefined}
              >
                <option value="">— Select —</option>
                {usTimezones.map((tz) => (
                    <option key={tz} value={tz}>{tz}</option>
                ))}
              </Field>
              <Form.Text className="text-muted">
                Session times at this location are displayed in this zone.
              </Form.Text>
              <ErrorMessage name="timezone">
                {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
              </ErrorMessage>
            </Form.Group>

            <Form.Group
                className="mb-lg-3 mb-1"
                controlId="scheduling_email"
            >
              <FormLabel required>Scheduling Email</FormLabel>
              <Field
                  name="scheduling_email"
                  type="text"
                  as={Form.Control}
                  isInvalid={touched.scheduling_email && !!errors.scheduling_email}
                  aria-required
              />
              <ErrorMessage name="scheduling_email">
                {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
              </ErrorMessage>
            </Form.Group>

            <Form.Group
                className="mb-lg-3 mb-1"
                controlId="invoicing_email"
            >
              <FormLabel>Invoicing Email</FormLabel>
              <Field
                  name="invoicing_email"
                  type="text"
                  as={Form.Control}
                  isInvalid={touched.invoicing_email && !!errors.invoicing_email}
              />
              <ErrorMessage name="invoicing_email">
                {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
              </ErrorMessage>
            </Form.Group>

            <Form.Group
                className="mb-lg-3 mb-1"
                controlId="hotline"
            >
              <FormLabel>Chef Hotline Number</FormLabel>
              <Field
                  name="hotline"
                  type="text"
                  as={Form.Control}
                  isInvalid={touched.hotline && !!errors.hotline}
              />
              <ErrorMessage name="hotline">
                {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
              </ErrorMessage>
            </Form.Group>

            <Form.Group
                className="mb-lg-3 mb-1"
                controlId="tax_id_number"
            >
              <FormLabel>Tax ID Number</FormLabel>
              <Field
                  name="tax_id_number"
                  type="text"
                  as={Form.Control}
                  isInvalid={touched.tax_id_number && !!errors.tax_id_number}
              />
              <ErrorMessage name="tax_id_number">
                {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
              </ErrorMessage>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col
              xs={12}
              className="pe-1"
          >
            <Form.Label className="h4">Location Information</Form.Label>
          </Col>

          <Col
              xs={6}
              className="pe-1"
          >
            <Form.Group
                className="mb-lg-3 mb-1"
                controlId="location_information_facebook"
            >
              <FormLabel>Facebook link</FormLabel>
              <Field
                  name="location_information_facebook"
                  type="text"
                  as={Form.Control}
                  isInvalid={
                      touched.location_information_facebook && !!errors.location_information_facebook
                  }
              />
              <ErrorMessage name="location_information_facebook">
                {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
              </ErrorMessage>
            </Form.Group>

            <Form.Group
                className="mb-lg-3 mb-1"
                controlId="location_information_franchise_owner_first_name"
            >
              <FormLabel>Owner First name</FormLabel>
              <Field
                  name="location_information_franchise_owner_first_name"
                  type="text"
                  as={Form.Control}
                  isInvalid={
                      touched.location_information_franchise_owner_first_name &&
                      !!errors.location_information_franchise_owner_first_name
                  }
              />
              <ErrorMessage name="location_information_franchise_owner_first_name">
                {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
              </ErrorMessage>
            </Form.Group>

            <Form.Group
                className="mb-lg-3 mb-1"
                controlId="location_information_franchise_owner_email"
            >
              <FormLabel>Location email</FormLabel>
              <Field
                  name="location_information_franchise_owner_email"
                  type="text"
                  as={Form.Control}
                  isInvalid={
                      touched.location_information_franchise_owner_email &&
                      !!errors.location_information_franchise_owner_email
                  }
              />
              <ErrorMessage name="location_information_franchise_owner_email">
                {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
              </ErrorMessage>
            </Form.Group>
          </Col>

          <Col
              xs={6}
              className="ps-1"
          >
            <Form.Group
                className="mb-lg-3 mb-1"
                controlId="location_information_instagram"
            >
              <FormLabel>Instagram link</FormLabel>
              <Field
                  name="location_information_instagram"
                  type="text"
                  as={Form.Control}
                  isInvalid={
                      touched.location_information_instagram && !!errors.location_information_instagram
                  }
              />
              <ErrorMessage name="location_information_instagram">
                {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
              </ErrorMessage>
            </Form.Group>

            <Form.Group
                className="mb-lg-3 mb-1"
                controlId="location_information_franchise_owner_last_name"
            >
              <FormLabel>Owner Last name</FormLabel>
              <Field
                  name="location_information_franchise_owner_last_name"
                  type="text"
                  as={Form.Control}
                  isInvalid={
                      touched.location_information_franchise_owner_last_name &&
                      !!errors.location_information_franchise_owner_last_name
                  }
              />
              <ErrorMessage name="location_information_franchise_owner_last_name">
                {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
              </ErrorMessage>
            </Form.Group>

            <Form.Group
                className="mb-lg-3 mb-1"
                controlId="location_information_jobs_url"
            >
              <FormLabel>Jobs URL</FormLabel>
              <Field
                  name="location_information_jobs_url"
                  type="text"
                  as={Form.Control}
                  isInvalid={
                      touched.location_information_jobs_url && !!errors.location_information_jobs_url
                  }
              />
              <ErrorMessage name="location_information_jobs_url">
                {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
              </ErrorMessage>
            </Form.Group>

            <Form.Group
                className="mb-lg-3 mb-1"
                controlId="consumer_fusion_business_id"
            >
              <FormLabel>ConsumerFusion Business ID</FormLabel>
              <Field
                  name="consumer_fusion_business_id"
                  type="text"
                  inputMode="numeric"
                  as={Form.Control}
                  isInvalid={
                      touched.consumer_fusion_business_id && !!errors.consumer_fusion_business_id
                  }
              />
              <Form.Text muted>
                Enables &quot;Send Review Requests&quot; on this location&apos;s finished sessions. Leave blank to disable.
              </Form.Text>
              <ErrorMessage name="consumer_fusion_business_id">
                {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
              </ErrorMessage>
            </Form.Group>
          </Col>

          <Col
              xs={12}
              lg={12}
              className="pe-1"
          >
            <Form.Group
                className="mb-lg-3 mb-1"
                controlId="location_information_who_we_are"
            >
              <FormLabel>Who We Are</FormLabel>
              <Field
                  name="location_information_who_we_are"
                  as="textarea"
                  className={`form-control ${touched.location_information_who_we_are && !!errors.location_information_who_we_are ? 'is-invalid' : ''}`}
                  maxLength={3000}
              />
              <div className="text-muted small text-end">
                {values?.location_information_who_we_are?.length || 0}/3000 characters
              </div>
              <ErrorMessage name="location_information_who_we_are">
                {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
              </ErrorMessage>
            </Form.Group>
          </Col>
        </Row>

        <FieldArray
            name="addresses"
            render={(arrayHelpers) => (
                <Form.Group
                    className="mb-lg-3 mb-1"
                    controlId="addresses"
                >
                  <Form.Label className="h4">Remittance Mailing Addresses</Form.Label>
                  <br />
                  <div className="multi-content-scroll mt-2">
                    <Row>
                      {values?.addresses && values?.addresses.length > 0 ? (
                          values.addresses.map((a, index) => {
                            let meta = {
                              touched: Array.isArray(touched.addresses) ? touched.addresses[index] : {},
                              error: Array.isArray(errors.addresses) ? (errors.addresses[index] ?? {}) : {}
                            };
                            return (
                                <Col
                                    key={index}
                                    lg={6}
                                    className="mb-2"
                                >
                                  <Card border="primary">
                                    {typeof errors.addresses === 'string' ? (
                                        <div>{errors.addresses}</div>
                                    ) : null}

                                    <Card.Body>
                                      <Col className="ms-1">
                                        <Row>
                                          <Col
                                              xs={6}
                                              className="pe-1"
                                          >
                                            <Form.Group
                                                className="mb-lg-3 mb-1"
                                                controlId={`addresses.${index}.street`}
                                            >
                                              <FormLabel required>Street</FormLabel>
                                              <Field
                                                  name={`addresses.${index}.street`}
                                                  as={Form.Control}
                                                  isInvalid={
                                                      meta.touched?.street &&
                                                      !!(typeof meta.error === 'string'
                                                          ? meta.error
                                                          : meta.error.street)
                                                  }
                                                  aria-required
                                              />
                                              <ErrorMessage name={`addresses.${index}.street`}>
                                                {(msg) => (
                                                    <FormControl.Feedback type="invalid">
                                                      {msg}
                                                    </FormControl.Feedback>
                                                )}
                                              </ErrorMessage>
                                            </Form.Group>
                                            <Form.Group
                                                className="mb-lg-3 mb-1"
                                                controlId={`addresses.${index}.unit`}
                                            >
                                              <FormLabel>Unit</FormLabel>
                                              <Field
                                                  name={`addresses.${index}.unit`}
                                                  type="text"
                                                  as={Form.Control}
                                                  isInvalid={
                                                      meta.touched?.unit &&
                                                      !!(typeof meta.error === 'string'
                                                          ? meta.error
                                                          : meta.error.unit)
                                                  }
                                              />
                                              <ErrorMessage name={`addresses.${index}.unit`}>
                                                {(msg) => (
                                                    <FormControl.Feedback type="invalid">
                                                      {msg}
                                                    </FormControl.Feedback>
                                                )}
                                              </ErrorMessage>
                                            </Form.Group>
                                            <Form.Group
                                                className="mb-lg-3 mb-1"
                                                controlId={`addresses.${index}.city`}
                                            >
                                              <FormLabel required>City</FormLabel>
                                              <Field
                                                  name={`addresses.${index}.city`}
                                                  type="text"
                                                  as={Form.Control}
                                                  isInvalid={
                                                      meta.touched?.city &&
                                                      !!(typeof meta.error === 'string'
                                                          ? meta.error
                                                          : meta.error.city)
                                                  }
                                                  aria-required
                                              />
                                              <ErrorMessage name={`addresses.${index}.city`}>
                                                {(msg) => (
                                                    <FormControl.Feedback type="invalid">
                                                      {msg}
                                                    </FormControl.Feedback>
                                                )}
                                              </ErrorMessage>
                                            </Form.Group>
                                            <Button
                                                variant="primary"
                                                size="sm"
                                                className="btn-box-shadow text-uppercase border-0"
                                                style={{ width: 84, background: '#B81F69', fontSize: '.75rem' }}
                                                onClick={() => arrayHelpers.remove(index)}
                                            >
                                              Remove
                                            </Button>
                                          </Col>
                                          <Col
                                              xs={6}
                                              className="ps-1"
                                          >
                                            <Form.Group
                                                className="mb-lg-3 mb-1"
                                                controlId={`addresses.${index}.state`}
                                            >
                                              <FormLabel required>State</FormLabel>
                                              <Field
                                                  name={`addresses.${index}.state`}
                                                  as={Form.Control}
                                                  isInvalid={
                                                      meta.touched?.state &&
                                                      !!(typeof meta.error === 'string'
                                                          ? meta.error
                                                          : meta.error.state)
                                                  }
                                                  aria-required
                                              />
                                              <ErrorMessage name={`addresses.${index}.state`}>
                                                {(msg) => (
                                                    <FormControl.Feedback type="invalid">
                                                      {msg}
                                                    </FormControl.Feedback>
                                                )}
                                              </ErrorMessage>
                                            </Form.Group>

                                            <Form.Group
                                                className="mb-lg-3 mb-1"
                                                controlId={`addresses.${index}.zip_code`}
                                            >
                                              <FormLabel required>Zip Code</FormLabel>
                                              <Field
                                                  name={`addresses.${index}.zip_code`}
                                                  type="tel"
                                                  as={Form.Control}
                                                  isInvalid={
                                                      meta.touched?.zip_code &&
                                                      !!(typeof meta.error === 'string'
                                                          ? meta.error
                                                          : meta.error.zip_code)
                                                  }
                                                  aria-required
                                              />
                                              <ErrorMessage name={`addresses.${index}.zip_code`}>
                                                {(msg) => (
                                                    <FormControl.Feedback type="invalid">
                                                      {msg}
                                                    </FormControl.Feedback>
                                                )}
                                              </ErrorMessage>
                                            </Form.Group>
                                          </Col>
                                        </Row>
                                      </Col>
                                    </Card.Body>
                                  </Card>
                                </Col>
                            );
                          })
                      ) : (
                          <></>
                      )}
                    </Row>
                  </div>
                  <button
                      type="button"
                      className="btn btn-sm btn-link fw-bolder text-uppercase link-primary link-underline-opacity-0"
                      onClick={() =>
                          arrayHelpers.insert(values?.addresses.length ?? 0, {
                            street: '',
                            unit: '',
                            city: '',
                            state: '',
                            zip_code: ''
                          })
                      }
                  >
                    + Add New Address
                  </button>
                </Form.Group>
            )}
        />

        <Row>
          <Col
              xs={6}
              className="pe-1"
          >
            <Form.Group
                className="mb-lg-3 mb-1"
                controlId="latitude"
            >
              <FormLabel required>Latitude</FormLabel>
              <Field
                  name="latitude"
                  as={Form.Control}
                  isInvalid={touched.latitude && !!errors.latitude}
                  aria-required
              />
              <ErrorMessage name="latitude">
                {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
              </ErrorMessage>
            </Form.Group>

            <Form.Group
                className="mb-lg-3 mb-1"
                controlId="zoom"
            >
              <FormLabel required>Zoom</FormLabel>
              <Field
                  name="zoom"
                  as={Form.Control}
                  isInvalid={touched.zoom && !!errors.zoom}
                  aria-required
              />
              <ErrorMessage name="zoom">
                {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
              </ErrorMessage>
            </Form.Group>
          </Col>

          <Col
              xs={6}
              className="pe-1"
          >
            <Form.Group
                className="mb-lg-3 mb-1"
                controlId="longitude"
            >
              <FormLabel required>Longitude</FormLabel>
              <Field
                  name="longitude"
                  as={Form.Control}
                  isInvalid={touched.longitude && !!errors.longitude}
                  aria-required
              />
              <ErrorMessage name="longitude">
                {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
              </ErrorMessage>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col
              xs={12}
              className="pe-1"
          >
            <Form.Label className="h4">Location images</Form.Label>
          </Col>
          {!readOnly && (
              <Col
                  xs={12}
                  lg={6}
              >
                <Stack gap={2}>
                  <h6
                      className="fw-bold mb-0"
                      style={{ fontSize: '1.125rem', color: '#5E6469' }}
                  >
                    Add an image
                  </h6>

                  <Form.Group controlId="document_type">
                    <FormLabel>Image type</FormLabel>
                    <Field
                        name="document_type"
                        as={Form.Select}
                        disabled={readOnly}
                        value={documentType}
                        onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                          const selected = event.target.value;
                          setDocumentType(selected);
                        }}
                    >
                      <option>Select</option>
                      <option value="images">Images</option>
                      <option value="franchise_images">Franchise Images</option>
                      <option value="contact_image">Contact Image</option>
                    </Field>
                    <ErrorMessage name="document_type">
                      {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
                    </ErrorMessage>
                  </Form.Group>

                  <div>
                    <FormLabel>File</FormLabel>
                    <Dropzone onDrop={onDrop} />
                  </div>

                  {selectedFile && (
                      <>
                        <div className="mt-3">
                          <h6 className="text-info fw-semibold">Selected File:</h6>
                          <p className="text-truncate mb-0">{selectedFile.name}</p>
                        </div>
                        <Button
                            variant="primary"
                            size="sm"
                            className="btn-box-shadow text-uppercase border-0"
                            style={{ width: 150 }}
                            onClick={() => {
                              let error = '';
                              let count = 0;
                              switch (documentType) {
                                case 'images':
                                  count = values!.images!.length;
                                  if (count >= 3) {
                                    error = 'Images max size is 3';
                                  } else {
                                    setFieldValue!('images', [...values!.images!, selectedFile]);
                                  }
                                  break;
                                case 'franchise_images':
                                  count = values!.franchise_images!.length;
                                  if (count >= 3) {
                                    error = 'Franchise Images max size is 3';
                                  } else {
                                    setFieldValue!('franchise_images', [
                                      ...values!.franchise_images!,
                                      selectedFile
                                    ]);
                                  }
                                  break;
                                case 'contact_image':
                                  setFieldValue!('contact_image', selectedFile);
                                  break;
                                default:
                                  error = 'Document type is not selected';
                                  break;
                              }

                              if (!error) {
                                setFileName('');
                                setDocumentType('');
                                setSelectedFile(undefined);
                              } else {
                                addToast(error, 'error');
                              }
                            }}
                        >
                          Add image
                        </Button>
                      </>
                  )}
                </Stack>
              </Col>
          )}
          <Col
              xs={12}
              lg={6}
          >
            <Stack gap={2}>
              <h6
                  className="fw-bold mb-0"
                  style={{ fontSize: '1.125rem', color: '#5E6469' }}
              >
                Images
              </h6>

              {values!.images!.length === 0 && <Form.Label>NOTHING ADDED YET</Form.Label>}
              {values!.images!.map((file, index) => (
                  <DocumentElement
                      key={index}
                      file={file}
                      name={file.name}
                      onRemove={() => {
                        setFieldValue!(
                            'images',
                            values!.images!.filter((_, i) => i !== index)
                        );
                      }}
                      disabled={readOnly ?? false}
                  />
              ))}
            </Stack>

            <Stack gap={2}>
              <h6
                  className="fw-bold mb-0"
                  style={{ fontSize: '1.125rem', color: '#5E6469' }}
              >
                Franchise Images
              </h6>

              {values!.franchise_images!.length === 0 && <Form.Label>NOTHING ADDED YET</Form.Label>}
              {values!.franchise_images!.map((file, index) => (
                  <DocumentElement
                      key={index}
                      file={file}
                      name={file.name}
                      onRemove={() => {
                        setFieldValue!(
                            'franchise_images',
                            values!.franchise_images!.filter((_, i) => i !== index)
                        );
                      }}
                      disabled={readOnly ?? false}
                  />
              ))}
            </Stack>

            <Stack gap={2}>
              <h6
                  className="fw-bold mb-0"
                  style={{ fontSize: '1.125rem', color: '#5E6469' }}
              >
                Contact Image
              </h6>

              {!values!.contact_image && <Form.Label>NOTHING ADDED YET</Form.Label>}
              {values!.contact_image && (
                  <DocumentElement
                      file={values!.contact_image}
                      name={values!.contact_image.name}
                      onRemove={() => {
                        setFieldValue!('contact_image', undefined);
                      }}
                      disabled={readOnly ?? false}
                  />
              )}
            </Stack>
          </Col>
        </Row>
        <NationwideMapSection values={values} setFieldValue={setFieldValue} />
      </>
  );
};

const US_STATE_NAMES: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DC: 'Washington, DC', DE: 'Delaware', FL: 'Florida', GA: 'Georgia',
  HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
  KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
  MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri',
  MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio',
  OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
  SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont',
  VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
};

interface NationwideMapSectionProps {
  values?: StepProps['values'];
  setFieldValue?: StepProps['setFieldValue'];
}

const NationwideMapSection: React.FC<NationwideMapSectionProps> = ({ values, setFieldValue }) => {
  const { data: groups } = useGetLocationMapGroupsQuery();
  const { data: allMapLocations } = useGetLocationsMapQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const [createGroup] = useCreateLocationMapGroupMutation();
  const [newGroupTitle, setNewGroupTitle] = useState('');
  const [showNewGroup, setShowNewGroup] = useState(false);

  const contextLocations = (allMapLocations ?? []).filter((loc) => loc.id !== values?.id);

  const handleCreateGroup = async () => {
    if (!newGroupTitle.trim()) return;
    try {
      const group = await createGroup({ title: newGroupTitle.trim() }).unwrap();
      setFieldValue?.('map_group_id', group.id);
      setNewGroupTitle('');
      setShowNewGroup(false);
    } catch {
      // error handled silently — group list will not update if it fails
    }
  };

  return (
      <Row className="mt-4">
        <Col xs={12} className="pe-1">
          <Form.Label className="h4">Nationwide Map</Form.Label>
        </Col>

        {/* Left column — fields */}
        <Col xs={12} lg={5}>
          <Form.Group className="mb-3" controlId="show_on_map">
            <Form.Check
                type="switch"
                label="Show on nationwide map"
                checked={values?.show_on_map ?? false}
                onChange={(e) => setFieldValue?.('show_on_map', e.target.checked)}
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="state">
            <FormLabel>State</FormLabel>
            <Field name="state" as={Form.Select}>
              <option value="">— None —</option>
              {Object.entries(US_STATE_NAMES).sort(([, a], [, b]) => a.localeCompare(b)).map(([code, name]) => (
                  <option key={code} value={code}>{name} ({code})</option>
              ))}
            </Field>
          </Form.Group>

          <Form.Group className="mb-3" controlId="map_pin_type">
            <FormLabel>Pin Type</FormLabel>
            <Field name="map_pin_type" as={Form.Select}>
              <option value="franchise">Franchise</option>
              <option value="outlet">Outlet</option>
            </Field>
          </Form.Group>

          <Form.Group className="mb-3" controlId="map_group_id">
            <FormLabel>Map Group</FormLabel>
            <Field name="map_group_id" as={Form.Select}
                   value={values?.map_group_id ?? ''}
                   onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                     const val = e.target.value;
                     setFieldValue?.('map_group_id', val === '' ? null : Number(val));
                   }}
            >
              <option value="">— None (solo pin) —</option>
              {groups?.map((g: { id: number; title: string }) => (
                  <option key={g.id} value={g.id}>{g.title}</option>
              ))}
            </Field>
          </Form.Group>

          {showNewGroup ? (
              <div className="d-flex gap-2 mb-3">
                <Form.Control
                    size="sm"
                    placeholder="New group name"
                    value={newGroupTitle}
                    onChange={(e) => setNewGroupTitle(e.target.value)}
                />
                <Button size="sm" variant="primary" onClick={handleCreateGroup}>
                  Save
                </Button>
                <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => { setShowNewGroup(false); setNewGroupTitle(''); }}
                >
                  Cancel
                </Button>
              </div>
          ) : (
              <button
                  type="button"
                  className="btn btn-sm btn-link fw-bolder text-uppercase link-primary link-underline-opacity-0 p-0 mb-3"
                  onClick={() => setShowNewGroup(true)}
              >
                + New group
              </button>
          )}

          <Row>
            <Col xs={6}>
              <Form.Group className="mb-3" controlId="map_x">
                <FormLabel>Map X (%)</FormLabel>
                <Field
                    name="map_x"
                    as={NumberInput}
                    placeholder="0–100"
                    value={values?.map_x ?? ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setFieldValue?.('map_x', e.target.value === '' ? null : Number(e.target.value))
                    }
                />
              </Form.Group>
            </Col>
            <Col xs={6}>
              <Form.Group className="mb-3" controlId="map_y">
                <FormLabel>Map Y (%)</FormLabel>
                <Field
                    name="map_y"
                    as={NumberInput}
                    placeholder="0–100"
                    value={values?.map_y ?? ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setFieldValue?.('map_y', e.target.value === '' ? null : Number(e.target.value))
                    }
                />
              </Form.Group>
            </Col>
          </Row>
        </Col>

        {/* Right column — pin dropper (uses same map component as /nationwide) */}
        <Col xs={12} lg={7}>
          <FormLabel>Click to place pin on map</FormLabel>
          <p className="text-muted small mb-2">
            Preview matches the nationwide page at any screen size. Faded pins show other locations.
          </p>
          <MapPinLayer
              key={values?.id ?? 'new'}
              mapImage={MapImg}
              locations={contextLocations}
              onSoloClick={() => {}}
              onGroupItemClick={() => {}}
              editable={{
                map_x: values?.map_x ?? null,
                map_y: values?.map_y ?? null,
                map_pin_type: values?.map_pin_type ?? 'franchise',
                onPlace: (x, y) => {
                  setFieldValue?.('map_x', x);
                  setFieldValue?.('map_y', y);
                },
              }}
          />
          {values?.map_x != null && values?.map_y != null && (
              <div className="text-muted small mt-1">
                Pin set at ({values.map_x}%, {values.map_y}%)
                {' '}
                <button
                    type="button"
                    className="btn btn-link btn-sm p-0 text-danger"
                    onClick={() => { setFieldValue?.('map_x', null); setFieldValue?.('map_y', null); }}
                >
                  Clear
                </button>
              </div>
          )}
        </Col>
      </Row>
  );
};

interface DropzoneProps {
  onDrop: Function;
}

const Dropzone: React.FC<DropzoneProps> = ({ onDrop }) => {
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (acceptedFiles) => onDrop(acceptedFiles),
    multiple: false
  });

  return (
      <div
          {...getRootProps({ className: 'dropzone' })}
          style={dropzoneStyle}
      >
        <input {...getInputProps()} />
        <Stack style={{ color: 'rgba(94, 100, 105, .5)' }}>
          <span>Drag and drop here</span>
          <span>or</span>
          <span
              className="fw-bold"
              style={{ color: '#6098B8' }}
          >
          Browse
        </span>
        </Stack>
      </div>
  );
};

const dropzoneStyle: React.CSSProperties = {
  border: '1px dashed rgba(94, 100, 105, 0.5)',
  borderRadius: 8,
  padding: 16,
  textAlign: 'center',
  cursor: 'pointer'
};
